import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Send } from "lucide-react";

const CATEGORIES = ["Bug", "Feature request", "Billing", "How-to question", "Other"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

export default function HelpTicketNew() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("How-to question");
  const [priority, setPriority] = useState("Normal");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 5 || subject.length > 120) {
      toast.error("Subject must be 5–120 characters."); return;
    }
    if (body.trim().length < 10 || body.length > 5000) {
      toast.error("Message must be 10–5000 characters."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/support/tickets", {
        subject: subject.trim(), category, priority, message_body: body,
      });
      toast.success("Ticket submitted");
      navigate(`/help/tickets/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell title="Submit a ticket">
      <div className="max-w-3xl mx-auto py-8 px-4">
        <Link to="/help/tickets" className="inline-flex items-center text-sm text-navy-500 hover:text-navy-900 mb-3" data-testid="ticket-new-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> My tickets
        </Link>
        <h1 className="text-2xl font-semibold mb-1" data-testid="ticket-new-heading">Submit a ticket</h1>
        <p className="text-sm text-navy-500 mb-6">
          Tell us what's going on. We reply in-app — you'll get a notification here on every update.
        </p>
        <form onSubmit={submit} className="eb-card p-6 space-y-4" data-testid="ticket-new-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-navy-500 font-semibold mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm"
              data-testid="ticket-new-subject"
              placeholder="Short summary of the issue"
              required
            />
            <div className="text-[10px] text-navy-400 mt-1">{subject.length} / 120</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-navy-500 font-semibold mb-1">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`ticket-new-category-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-navy-500 font-semibold mb-1">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="ticket-new-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-navy-500 font-semibold mb-1">Message (Markdown supported)</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={5000}
              rows={8}
              className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm font-mono"
              data-testid="ticket-new-body"
              placeholder="What were you doing? What happened? Anything we should know?"
              required
            />
            <div className="text-[10px] text-navy-400 mt-1">{body.length} / 5000 · Markdown</div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="ticket-new-submit">
              <Send className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Submitting…" : "Submit ticket"}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
